import type {LoaderFunctionArgs} from "@remix-run/node";
import {redirect} from "@remix-run/node";
import {useLoaderData} from "@remix-run/react";
import {Ad, BASE_AD_TAG} from "@/components/adSections/index";
import {headerTypes, pagesComponent} from "@/components/pages/index";
import {sectionsComponents} from "@/components/sections/index";
import {PAGES_TEMPLATES} from "@/constants/index";
import type {Page as ContentPage, Section} from "@/generated/api";
import {usePageAds} from "@/hooks/usePageAds";
import {usePageview} from "@/hooks/usePageview";
import BasePage from "@/layouts/BasePage";
import {newsLetterInaction} from "@/session";
import {compareSlugs, listedPosts, relativeLinkOf} from "@/utils/index";
import {clientLoaderWithTexts} from "@/utils/markdown/clientLoader";
import {getMeta} from "@/utils/meta";
import {fetchPageContent} from "@/utils/pageContent";
import {getPagesTemplates} from "@/utils/pages";
import {extractPageTitle} from "@/utils/pageTitle";
import {sectionAnchorProps} from "@/utils/sectionAnchor";
import {getB16Section, handleB16Response, handlePagination} from "@/utils/sections/b16";
import {getShell} from "@/utils/shell";
import {decodeHtmlEntities, removeTrailingSlash, trailingSlashExist} from "@/utils/text";

// Reached by a click, the page gets its data from the server loader and the compiler for its texts together
export const clientLoader = clientLoaderWithTexts;

export async function loader({request, params, context}: LoaderFunctionArgs) {
  const {siteUrl, language, RECAPTCHA_SITE, redirectUrls, basename} = context;
  const fullSlug = params["*"];
  const baseApiUrl = process.env.API_BASE_URL as string;
  const url = new URL(request.url);
  const cookieHeader = request.headers.get("Cookie");

  const newsLetterInactionValue = await newsLetterInaction.parse(cookieHeader);

  const formattedSlug = removeTrailingSlash(fullSlug);
  const queryParams = new URLSearchParams(url.search);
  const paramValue = queryParams.get("page");
  let queryPage = "";
  if (paramValue) {
    queryPage = `/?page=${paramValue}`;
  }

  const needRedirect = redirectUrls?.find(
    (item: {oldUrl: string; newUrl: string | number}) =>
      item.oldUrl === `${formattedSlug}${queryPage}`,
  );

  if (needRedirect) {
    const {newUrl} = needRedirect;
    if (newUrl === 404) {
      return redirect("/", 301);
    }

    return redirect(newUrl, 301);
  }
  if (trailingSlashExist(fullSlug)) {
    return redirect(`/${formattedSlug}`, 301);
  }

  // A splat always has a path ; without one there is no page to ask for
  if (!formattedSlug) {
    throw new Response(null, {status: 404, statusText: "Not Found"});
  }

  const data = await fetchPageContent<ContentPage[]>({
    baseApiUrl,
    language,
    path: formattedSlug,
  });

  // A page that WordPress does not have has no template: it is a 404. (The default template is "", one of PAGES_TEMPLATES: an absent template
  // must not be read as that one, or every unknown URL would be served as an empty page)
  const template = data?.[0]?.template;
  if (template === undefined || !PAGES_TEMPLATES.includes(template)) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }

  const correctSlug = relativeLinkOf(data?.[0]?.front_links);
  if (correctSlug) {
    const isSlugCorrect = compareSlugs(correctSlug, fullSlug as string);
    if (!isSlugCorrect) {
      throw new Response(null, {
        status: 404,
        statusText: "Not Found",
      });
    }
  }
  const defaultCurrentPage = listedPosts(data?.[0]?.page_content)?.current_page;
  const defaultTotalPages = listedPosts(data?.[0]?.page_content)?.nb_total_page;

  const b16 = getB16Section(data);

  const b16Response = await handleB16Response({
    url,
    language,
    baseApiUrl,
    b16: b16 ? b16 : listedPosts(data?.[0]?.page_content),
    adEnabled: data?.[0]?.ads_enabled,
  });
  const {queriedArticlesResponse, paginationLink} = b16Response;

  const pagination = handlePagination(
    queriedArticlesResponse?.totalPages || (b16 ? b16?.nb_total_page : defaultTotalPages),
    queriedArticlesResponse?.currentPage ||
      (b16 ? b16?.current_page : defaultCurrentPage),
  );

  // The chrome was started with the request: it runs beside the page call above (see utils/shell.ts)
  const {translation} = await getShell(context);
  return {
    data,
    sections: data?.[0] || null,
    translation,
    pagination,
    language,
    queriedArticlesResponse,
    RECAPTCHA_SITE,
    paginationLink,
    slug: params["*"],
    newsLetterInactionValue,
    routeBase: "",
    origin: siteUrl,
    baseApiUrl,
    basename,
  };
}

export const meta = ({data}: {data?: RouteLoaderData}) =>
  getMeta({
    page: data?.sections,
    adminOrigin: data?.baseApiUrl,
    frontOrigin: data?.origin,
  });

export default function Splat() {
  const data = useLoaderData() as RouteLoaderData;
  const sections = data?.sections;
  const adsEnabled = sections?.ads_enabled;

  const template = sections?.template;
  const {destinationsPage, legalPage, articlesPage, categoryPage, contactPage, faqPage} =
    getPagesTemplates(template);

  const TemplateComponent = pagesComponent?.[template];
  const translation = data?.translation;
  const sectionData = sections?.acf?.sections_builder;
  const breadcrumb = sections?.breadcrumb;

  const pageName = decodeHtmlEntities(sections?.title?.rendered) as string | undefined;

  // Extract page title from WordPress data
  const pageTitle = extractPageTitle(sections, data?.data, "page") ?? undefined;

  const headerData = {
    id: sections?.id,
    title: decodeHtmlEntities(sections?.title?.rendered),
    credit_desktop: sections?.acf?.credit,
    desktop_image: sections?.acf?.desktop_image,
    mobile_image: sections?.acf?.mobile_image,
    logo: sections?.acf?.logo,
    text: sections?.acf?.text,
  };

  usePageview(pageName);

  usePageAds(adsEnabled);

  return (
    <BasePage
      isFaqPage={faqPage}
      isContactPage={contactPage}
      isDestinationsPage={articlesPage || destinationsPage}
      isDestinationPage={categoryPage}
      headerData={categoryPage ? headerData : null}
      isLegalPage={legalPage}
      breadcrumb={breadcrumb}
      headerType={headerTypes?.[template]}
      translation={translation}
      pageTitle={pageTitle}
    >
      {TemplateComponent && <TemplateComponent />}
      {/*{(destinationsPage || articlesPage) && TemplateComponent && (*/}
      {/*  <Ad type="1000*90" tag={BASE_AD_TAG["728*90"]} showAd={adsEnabled} />*/}
      {/*)}*/}
      {sectionData &&
        !!sectionData.length &&
        sectionData.map((section: Section, index: number) => {
          let pushMailAd = 0;
          if (section.acf_fc_layout === "section--push-vm") {
            pushMailAd += 1;
          }

          const SectionComponent = sectionsComponents[section.acf_fc_layout];
          const id = `${section.acf_fc_layout}-${index}`;
          const isFirstSection = index === 0;
          const isLastSection = index === sectionData.length - 1;
          const isB16 = section.acf_fc_layout === "section--push-content";

          return SectionComponent ? (
            <div key={id} {...sectionAnchorProps(section)}>
              <SectionComponent
                key={id}
                id={id}
                index={sectionData.length - index + 2}
                queriedArticlesResponse={isB16 ? data?.queriedArticlesResponse : null}
                b10AdTag={pushMailAd}
                translation={translation}
                ad_enabled={adsEnabled}
                sectionData={section}
                isFirstSection={isFirstSection}
                isLastSection={isLastSection}
              />
              {index === 1 && categoryPage && <Ad type="970*250" showAd={adsEnabled} />}
              {/*{index === sectionData.length - 2 && categoryPage && (*/}
              {/*  <Ad type="970*250" showAd={adsEnabled} />*/}
              {/*)}*/}
              {!contactPage && !legalPage && index === sectionData.length - 2 && (
                <Ad type="1000*90" tag={BASE_AD_TAG["728*90"]} showAd={adsEnabled} />
              )}
            </div>
          ) : null;
        })}
    </BasePage>
  );
}
